/**
 * BillingPlanManagement.tsx
 * 빌링 플랜 관리 페이지 (Admin)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  billingPlanRepository,
  BillingPlan,
  CreatePlanRequest,
  UpdatePlanRequest,
} from '../../repositories/BillingPlanRepository';

// ============================================================================
// Styles
// ============================================================================

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a1a1a',
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  planGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
  },
  planCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e5e7eb',
  },
  planCardInactive: {
    opacity: 0.6,
    backgroundColor: '#f9fafb',
  },
  planName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '8px',
  },
  planDescription: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '16px',
  },
  planDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '16px',
  },
  planDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
  },
  detailLabel: {
    color: '#6b7280',
  },
  detailValue: {
    fontWeight: '500',
    color: '#1a1a1a',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
  activeBadge: {
    backgroundColor: '#d1fae5',
    color: '#059669',
  },
  inactiveBadge: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  editButton: {
    flex: 1,
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  deleteButton: {
    padding: '8px 16px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#1a1a1a',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    minHeight: '80px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#6b7280',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
};

// ============================================================================
// Component
// ============================================================================

export const BillingPlanManagement: React.FC = () => {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BillingPlan | null>(null);
  const [formData, setFormData] = useState<CreatePlanRequest>({
    name: '',
    description: '',
    monthly_base_fee: 0,
    alimtalk_unit_price: 13,
    alimtalk_free_quota: 0,
  });

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingPlanRepository.getAllPlans(true);
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '플랜을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleOpenModal = (plan?: BillingPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description || '',
        monthly_base_fee: plan.monthly_base_fee,
        alimtalk_unit_price: plan.alimtalk_unit_price,
        alimtalk_free_quota: plan.alimtalk_free_quota,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        monthly_base_fee: 0,
        alimtalk_unit_price: 13,
        alimtalk_free_quota: 0,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingPlan) {
        const updateData: UpdatePlanRequest = {
          name: formData.name,
          description: formData.description,
          monthly_base_fee: formData.monthly_base_fee,
          alimtalk_unit_price: formData.alimtalk_unit_price,
          alimtalk_free_quota: formData.alimtalk_free_quota,
        };
        await billingPlanRepository.updatePlan(editingPlan.id, updateData);
      } else {
        await billingPlanRepository.createPlan(formData);
      }
      handleCloseModal();
      loadPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장에 실패했습니다');
    }
  };

  const handleDelete = async (plan: BillingPlan) => {
    if (!window.confirm(`'${plan.name}' 플랜을 비활성화하시겠습니까?`)) {
      return;
    }

    try {
      await billingPlanRepository.deletePlan(plan.id);
      loadPlans();
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제에 실패했습니다');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  if (loading) {
    return <div style={styles.loading}>플랜을 불러오는 중...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>빌링 플랜 관리</h1>
        <button
          style={styles.addButton}
          onClick={() => handleOpenModal()}
        >
          + 새 플랜 추가
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.planGrid}>
        {plans.map((plan) => (
          <div
            key={plan.id}
            style={{
              ...styles.planCard,
              ...(plan.is_active ? {} : styles.planCardInactive),
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <h3 style={styles.planName}>{plan.name}</h3>
              <span
                style={{
                  ...styles.statusBadge,
                  ...(plan.is_active ? styles.activeBadge : styles.inactiveBadge),
                }}
              >
                {plan.is_active ? '활성' : '비활성'}
              </span>
            </div>

            {plan.description && (
              <p style={styles.planDescription}>{plan.description}</p>
            )}

            <div style={styles.planDetails}>
              <div style={styles.planDetail}>
                <span style={styles.detailLabel}>월 기본료</span>
                <span style={styles.detailValue}>
                  {formatCurrency(plan.monthly_base_fee)}
                </span>
              </div>
              <div style={styles.planDetail}>
                <span style={styles.detailLabel}>알림톡 단가</span>
                <span style={styles.detailValue}>
                  {plan.alimtalk_unit_price}원/건
                </span>
              </div>
              <div style={styles.planDetail}>
                <span style={styles.detailLabel}>무료 건수</span>
                <span style={styles.detailValue}>
                  {plan.alimtalk_free_quota.toLocaleString()}건
                </span>
              </div>
            </div>

            <div style={styles.cardActions}>
              <button
                style={styles.editButton}
                onClick={() => handleOpenModal(plan)}
              >
                수정
              </button>
              {plan.is_active && (
                <button
                  style={styles.deleteButton}
                  onClick={() => handleDelete(plan)}
                >
                  비활성화
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modal} onClick={handleCloseModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {editingPlan ? '플랜 수정' : '새 플랜 추가'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>플랜 이름 *</label>
                <input
                  type="text"
                  style={styles.input}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="예: Basic, Standard, Premium"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>설명</label>
                <textarea
                  style={styles.textarea}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="플랜에 대한 설명을 입력하세요"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>월 기본료 (원)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.monthly_base_fee}
                  onChange={(e) => setFormData({ ...formData, monthly_base_fee: Number(e.target.value) })}
                  min="0"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>알림톡 단가 (원/건)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.alimtalk_unit_price}
                  onChange={(e) => setFormData({ ...formData, alimtalk_unit_price: Number(e.target.value) })}
                  min="0"
                  step="0.01"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>무료 건수</label>
                <input
                  type="number"
                  style={styles.input}
                  value={formData.alimtalk_free_quota}
                  onChange={(e) => setFormData({ ...formData, alimtalk_free_quota: Number(e.target.value) })}
                  min="0"
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.cancelButton}
                  onClick={handleCloseModal}
                >
                  취소
                </button>
                <button type="submit" style={styles.submitButton}>
                  {editingPlan ? '저장' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPlanManagement;
